import React from 'react';
import { Crop } from 'react-image-crop';

import { Size } from '../../typings/common';
import { GalleriesRequestData } from '../GalleryPane/typings';

import { createCreativeSize } from './mappers';

export enum Order {
    ASC = 'asc',
    DESC = 'desc'
}

export enum CreativeType {
    COMMON = 'common',
    VIDEO_ADDITION = 'videoAddition',
    VIDEO_ADDITION_BAD = 'video-addition',
    CPM_GEOPRODUCT = 'cpmGeoproduct',
    CPM_GEOPRODUCT_BAD = 'cpm-geoproduct',
    ADAPTIVE = 'adaptive',
    OVERLAY = 'overlay'
}

export interface ApiMetaDataInfo {
    width: number;
    height: number;
    duration?: null | number;
    colorInfo?: Record<string, string>;
}

export const ADAPTIVE_SIZE = 'adaptive';

export const ALL_SIZES = {
    [CreativeType.COMMON]: [
        ADAPTIVE_SIZE,
        '300x250',
        '240x400',
        '320x50',
        '728x90',
        '300x300',
        '1000x120',
        '240x600',
        '300x500',
        '300x600',
        '336x280',
        '320x480',
        '480x320'
    ],
    [CreativeType.CPM_GEOPRODUCT]: ['320x50', '320x67', '640x100', '640x134']
};

export enum ProductType {
    GENERAL = 'GENERAL',
    CPM_YNDX_FRONTPAGE = 'CPM_YNDX_FRONTPAGE',
    CPM_GEOPRODUCT = 'CPM_GEOPRODUCT'
}

const ALL_HTML5_SIZES = {
    [ProductType.GENERAL]: [
        '160x600',
        '240x400',
        '240x600',
        '300x250',
        '300x300',
        '300x500',
        '300x600',
        '320x50',
        '320x100',
        '320x480',
        '336x280',
        '480x320',
        '728x90',
        '970x250',
        '1000x120'
    ],
    [ProductType.CPM_YNDX_FRONTPAGE]: ['1456x180', '728x90', '640x134'],
    [ProductType.CPM_GEOPRODUCT]: ['320x50', '320x67', '640x100', '640x134']
};

export function getHtml5SelectedSizes(filters?: PresetFilters): string[] {
    if (filters && filters.sizes) {
        return filters.sizes.map(size => createCreativeSize(size));
    }

    return [];
}

export function getHtml5AllSizes(productType: ProductType = ProductType.GENERAL): string[] {
    return ALL_HTML5_SIZES[productType];
}

export type PresetFilters = {
    sizes: ({ w: number; h: number } | typeof ADAPTIVE_SIZE)[];
};

export const ADAPTIVE_SIZES = [
    { width: 300, height: 250 },
    { width: 240, height: 400 },
    { width: 240, height: 600 },
    { width: 300, height: 300 },
    { width: 300, height: 500 },
    { width: 300, height: 600 },
    // {width: 320, height: 50}, // TODO: вернуть этот размер после https://st.yandex-team.ru/DIRECT-99950
    { width: 320, height: 480 },
    { width: 336, height: 280 },
    { width: 480, height: 320 },
    { width: 728, height: 90 },
    { width: 1000, height: 120 }
];

export type SharedData = {
    clientId: string;
    creativeId?: string;
    creativeType?: CreativeType;
    areFiltersDisabled?: boolean;
    filters?: PresetFilters;
    cpc?: 1;
    cpm?: 1;
    cpm_outdoor?: 1;
    cpm_indoor?: 1;
    cpm_geoproduct?: 1;
    cpm_audio?: 1;
    mobile_content_video?: number;
    isCompact?: boolean;
    productType?: ProductType;
    isAdaptive?: boolean;
};

export type Permissions = {
    idea?: boolean;
};

export enum Page {
    CREATIVES = 'creatives',
    PRESETS = 'presets',
    CONSTRUCTOR = 'constructor',
    IDEAS = 'ideas'
}

export interface CreativeImageCropData {
    crop: Crop;
    minWidth?: number;
    minHeight?: number;
}

export interface CreativeImageData {
    src: string;
    resourceId: string;
    realImageSize?: Size;
    naturalImageSize?: Size;
}

export const DEFAULT_FETCH_LIMIT = 20;

export const GALLERY_DEFAULT_FETCH_OFFSET = 0;
export const GALLERY_FETCH_OFFSET_STEP = DEFAULT_FETCH_LIMIT;
export const GALLERY_DEFAULT_FETCH_ORDER = Order.DESC;
export const GALLERY_DEFAULT_CATEGORIES: string[] = [];
export const GALLERY_DEFAULT_FETCH_FILTER_NAME = '';
export const GALLERY_DEFAULT_SEMANTIC_TEXT_FILTER = '';
export const GALLERY_Z_INDEX_LEVEL = 2000;
export const GALLERY_MODAL_PREVIEW_Z_INDEX_LEVEL = 3000;
export const GALLERY_HOVER_PREVIEW_Z_INDEX_LEVEL = 3000;

export enum ResourceType {
    image = 'image',
    video = 'video',
    audio = 'audio',
    overlay = 'overlay'
}

export const DEFAULT_GALLERY_FILES_REQUEST: GalleriesRequestData = {
    offset: GALLERY_DEFAULT_FETCH_OFFSET,
    limit: DEFAULT_FETCH_LIMIT,
    name: GALLERY_DEFAULT_FETCH_FILTER_NAME,
    semantic_search: GALLERY_DEFAULT_SEMANTIC_TEXT_FILTER,
    sort_order: GALLERY_DEFAULT_FETCH_ORDER
};

export type CanvasAppContextProps = {
    parent: HTMLElement | null | (() => HTMLElement);
};

export const CanvasAppContext = React.createContext<CanvasAppContextProps>({ parent: null });

export const initialSharedData = {
    clientId: '',
    productType: ProductType.GENERAL,
    creativeType: CreativeType.COMMON
};

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends Array<infer U>
        ? Array<DeepPartial<U>>
        : T[P] extends ReadonlyArray<infer V>
        ? ReadonlyArray<DeepPartial<V>>
        : DeepPartial<T[P]>;
};
